import type { Affiliation, MeUser } from '@tomato/shared';
import {
  AFFILIATION_LABELS,
  AFFILIATIONS,
  checkNickname,
  checkWalletAddress,
  NICKNAME_CHANGE_LIMIT,
  NICKNAME_ERROR_MESSAGES,
  NICKNAME_RULES,
  nicknameWidth,
  WALLET_ADDRESS_MAX,
} from '@tomato/shared';
import type { ReactNode } from 'react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { formatTomato, SUGGESTION_LIMITS } from '@tomato/shared';

import { api, ApiError } from '../api/client';
import { InvitePanel } from '../components/InvitePanel';
import { WalletPanel } from '../components/WalletPanel';
import { Button, Checkbox, Label, Notice, Select, Spinner, TextArea, TextField } from '../components/ui';
import { deviceHint, isAndroid, isInAppBrowser, isIOS, isStandalone } from '../lib/browserEnv';
import { isUserCancel, passkeySupported, registerPasskey } from '../lib/passkey';
import type { ThemeChoice } from '../lib/theme';
import { getThemeChoice, setThemeChoice } from '../lib/theme';
import { useSessionStore } from '../store/session';

interface TelegramStatus {
  linked: boolean;
  /** 봇이 아직 설정되지 않았으면 false */
  available: boolean;
}

function TelegramPanel() {
  const [status, setStatus] = useState<TelegramStatus | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [note, setNote] = useState('');

  async function load() {
    setStatus(await api.get<TelegramStatus>('/api/settings/telegram'));
  }

  useEffect(() => {
    void load().catch(() => setError('상태를 불러오지 못했어요.'));
  }, []);

  async function link() {
    setBusy(true);
    setError('');
    try {
      const res = await api.post<{ url: string; expiresInMinutes: number }>(
        '/api/settings/telegram/link',
      );
      // 새 탭으로 연다. 텔레그램 앱이 설치돼 있으면 앱이 뜬다.
      window.open(res.url, '_blank', 'noopener');
      setNote(
        `텔레그램에서 "시작"을 눌러주세요. 링크는 ${res.expiresInMinutes}분 뒤 만료돼요. 연결 후 새로고침하면 상태가 바뀝니다.`,
      );
    } catch (err) {
      setError(err instanceof ApiError ? err.message : '링크를 만들지 못했어요.');
    } finally {
      setBusy(false);
    }
  }

  async function unlink() {
    setBusy(true);
    setError('');
    try {
      await api.del('/api/settings/telegram');
      setNote('');
      await load();
    } catch {
      setError('해제하지 못했어요.');
    } finally {
      setBusy(false);
    }
  }

  if (!status) {
    return (
      <div className="py-8 text-center">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="rounded-2xl bg-surface p-5 shadow-card">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-[15px] font-medium text-ink-900">텔레그램 알림</p>
          <p className="mt-1 text-[13px] leading-relaxed text-ink-500">
            내 글에 댓글이 달리면 텔레그램으로 알려드려요.
          </p>
        </div>
        {status.linked ? (
          <span className="shrink-0 rounded-md bg-ink-100 px-2 py-1 text-[11px] font-medium text-ink-600">
            연결됨
          </span>
        ) : null}
      </div>

      <div className="mt-4">
        {!status.available ? (
          <p className="text-[13px] text-ink-400">아직 준비 중이에요.</p>
        ) : status.linked ? (
          <Button variant="secondary" size="sm" disabled={busy} onClick={() => void unlink()}>
            연결 해제
          </Button>
        ) : (
          <Button size="sm" disabled={busy} onClick={() => void link()}>
            {busy ? '준비 중…' : '텔레그램 알림 받기'}
          </Button>
        )}
      </div>

      <Notice tone="info">{note}</Notice>
      <Notice>{error}</Notice>

      <p className="mt-4 text-[12px] leading-relaxed text-ink-400">
        알림에는 활동명과 글 제목만 담겨요. 이메일이나 비공개 계열사는 절대 들어가지 않습니다.
      </p>
    </div>
  );
}

interface NotifPrefs {
  onComment: boolean;
  onReply: boolean;
  onFavorite: boolean;
  onChat: boolean;
  onMenu: boolean;
}

/** 화면에 노출할 항목만 둔다. onMenu는 기능을 보류해 두어 여기에 없다. */
const NOTIF_LABELS: Partial<Record<keyof NotifPrefs, string>> = {
  onComment: '내 글에 댓글이 달렸을 때',
  onReply: '내 댓글에 답글이 달렸을 때',
  onFavorite: '찜한 글의 거래 상태·가격이 바뀌었을 때',
  onChat: '새 채팅 메시지가 왔을 때',
};

function NotifPanel() {
  const [prefs, setPrefs] = useState<NotifPrefs | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    void api
      .get<{ prefs: NotifPrefs }>('/api/settings/notifications')
      .then((res) => setPrefs(res.prefs))
      .catch(() => setError('설정을 불러오지 못했어요.'));
  }, []);

  async function toggle(key: keyof NotifPrefs, value: boolean) {
    if (!prefs) return;
    const next = { ...prefs, [key]: value };
    setPrefs(next); // 낙관적 반영. 실패하면 되돌린다.
    setBusy(true);
    try {
      const res = await api.patch<{ prefs: NotifPrefs }>('/api/settings/notifications', next);
      setPrefs(res.prefs);
    } catch {
      setPrefs(prefs);
      setError('저장하지 못했어요.');
    } finally {
      setBusy(false);
    }
  }

  if (!prefs) return null;

  return (
    <div className="rounded-2xl bg-surface p-5 shadow-card">
      <p className="text-[15px] font-medium text-ink-900">알림 종류</p>
      <p className="mt-1 text-[13px] text-ink-500">텔레그램을 연결했을 때만 발송돼요.</p>
      <div className="mt-4 space-y-3">
        {(Object.keys(NOTIF_LABELS) as (keyof NotifPrefs)[]).map((key) => (
          <Checkbox
            key={key}
            checked={prefs[key]}
            disabled={busy}
            onChange={(v) => void toggle(key, v)}
          >
            {NOTIF_LABELS[key]}
          </Checkbox>
        ))}
      </div>
      <Notice>{error}</Notice>
    </div>
  );
}

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

/** 한 단계 = 번호 + 설명. 스마트폰 안내를 또렷하게. */
function Step({ n, children }: { n: number; children: ReactNode }) {
  return (
    <li className="flex gap-2.5">
      <span className="flex size-5 shrink-0 items-center justify-center rounded-full bg-ink-100 text-[11px] font-bold text-ink-600">
        {n}
      </span>
      <span className="text-[13px] leading-relaxed text-ink-700">{children}</span>
    </li>
  );
}

/**
 * 홈 화면에 추가(PWA 설치) 안내 — **스마트폰 중심**.
 * 아이폰(사파리·크롬)과 안드로이드(크롬)를 나눠서 설명하고,
 * 카톡 등 인앱 브라우저면 먼저 기본 브라우저로 열라고 안내한다.
 */
function InstallPanel() {
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(null);
  const [installed, setInstalled] = useState(false);

  const ios = isIOS();
  const android = isAndroid();
  const inApp = isInAppBrowser();

  useEffect(() => {
    // 안드로이드·데스크톱 Chrome: 브라우저가 설치 가능 시점에 이 이벤트를 준다.
    const onPrompt = (e: Event) => {
      e.preventDefault();
      setDeferred(e as BeforeInstallPromptEvent);
    };
    const onInstalled = () => {
      setInstalled(true);
      setDeferred(null);
    };
    window.addEventListener('beforeinstallprompt', onPrompt);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onPrompt);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  // 이미 앱으로 실행 중이면 안내할 게 없다.
  if (isStandalone()) {
    return (
      <div className="rounded-2xl bg-surface p-5 shadow-card">
        <p className="text-[15px] font-medium text-ink-900">앱으로 실행 중이에요 🍅</p>
        <p className="mt-1 text-[13px] text-ink-500">홈 화면 아이콘으로 잘 들어오셨어요.</p>
      </div>
    );
  }

  return (
    <div className="rounded-2xl bg-surface p-5 shadow-card">
      <p className="text-[15px] font-medium text-ink-900">📱 홈 화면에 추가 (앱처럼 쓰기)</p>
      <p className="mt-1 text-[13px] leading-relaxed text-ink-500">
        홈 화면에 아이콘을 두면 앱처럼 전체화면으로 한 번에 열려요. 설치 용량은 거의 없어요.
      </p>

      {installed && (
        <p className="mt-4 text-[13px] text-tomato-600">설치됐어요. 홈 화면에서 열어보세요 🎉</p>
      )}

      {/* 카톡 등 인앱 브라우저 — 여기선 설치가 안 된다. 먼저 기본 브라우저로. */}
      {inApp && !installed && (
        <div className="mt-4 rounded-xl bg-amber-50 p-3.5">
          <p className="text-[13px] font-medium text-amber-800">
            먼저 크롬·사파리로 열어주세요
          </p>
          <p className="mt-1 text-[12px] leading-relaxed text-amber-700">
            지금은 앱 안 브라우저(카톡 등)라 홈 화면 추가·패스키가 막혀 있어요. 오른쪽 위(갤럭시는
            오른쪽 아래) <b>메뉴(⋮ 또는 ⋯)</b>에서 <b>“다른 브라우저로 열기”</b>(아이폰은{' '}
            <b>“Safari로 열기”</b>)를 누른 뒤, 아래 방법으로 추가하세요.
          </p>
        </div>
      )}

      {/* 데스크톱/안드로이드 Chrome이 설치 가능하다고 알려주면 바로 버튼 */}
      {deferred && !installed && (
        <div className="mt-4">
          <Button
            size="sm"
            onClick={() => {
              void deferred.prompt();
              void deferred.userChoice.finally(() => setDeferred(null));
            }}
          >
            지금 바로 설치
          </Button>
          <p className="mt-1.5 text-[12px] text-ink-400">버튼이 안 보이면 아래 방법으로 하세요.</p>
        </div>
      )}

      {/* 아이폰 */}
      {(ios || !android) && (
        <div className="mt-4">
          <p className="text-[13px] font-semibold text-ink-800">아이폰 (사파리·크롬 공통)</p>
          <ol className="mt-2 space-y-1.5">
            <Step n={1}>
              화면 아래(크롬은 위)의 <b>공유 버튼</b> <span className="text-ink-400">⬆️(네모에 화살표)</span>를 누르세요.
            </Step>
            <Step n={2}>
              목록을 내려 <b>“홈 화면에 추가”</b>를 고르세요.
            </Step>
            <Step n={3}>
              오른쪽 위 <b>“추가”</b>를 누르면 끝이에요.
            </Step>
          </ol>
        </div>
      )}

      {/* 안드로이드 */}
      {(android || !ios) && (
        <div className="mt-4">
          <p className="text-[13px] font-semibold text-ink-800">안드로이드 (크롬)</p>
          <ol className="mt-2 space-y-1.5">
            <Step n={1}>
              오른쪽 위 <b>메뉴(⋮)</b>를 누르세요.
            </Step>
            <Step n={2}>
              <b>“앱 설치”</b> 또는 <b>“홈 화면에 추가”</b>를 고르세요.
            </Step>
            <Step n={3}>
              <b>“설치/추가”</b>를 누르면 끝이에요.
            </Step>
          </ol>
          <p className="mt-2 text-[12px] text-ink-400">
            삼성 인터넷도 비슷해요: 메뉴 → “현재 페이지 추가” → “홈 화면”.
          </p>
        </div>
      )}
    </div>
  );
}

interface Passkey {
  id: string;
  deviceLabel: string | null;
  createdAt: string;
  lastUsedAt: string | null;
}

function PasskeyPanel() {
  const supported = passkeySupported();
  const [list, setList] = useState<Passkey[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [note, setNote] = useState('');

  async function load() {
    const res = await api.get<{ passkeys: Passkey[] }>('/api/passkeys');
    setList(res.passkeys);
  }

  useEffect(() => {
    if (!supported) return;
    void load().catch(() => setError('상태를 불러오지 못했어요.'));
  }, [supported]);

  async function add() {
    setBusy(true);
    setError('');
    setNote('');
    try {
      // 기기 이름은 사용자가 알아보기 쉽게 브라우저/OS에서 유추한 짧은 라벨을 붙인다.
      await registerPasskey(deviceHint());
      setNote('이 기기에 패스키를 등록했어요. 다음부터 인증번호 없이 로그인할 수 있어요.');
      await load();
    } catch (err) {
      if (isUserCancel(err)) return;
      setError(err instanceof ApiError ? err.message : '패스키를 등록하지 못했어요.');
    } finally {
      setBusy(false);
    }
  }

  async function remove(id: string) {
    setBusy(true);
    setError('');
    try {
      await api.del(`/api/passkeys/${id}`);
      await load();
    } catch {
      setError('삭제하지 못했어요.');
    } finally {
      setBusy(false);
    }
  }

  if (!supported) return null;

  return (
    <div className="rounded-2xl bg-surface p-5 shadow-card">
      <p className="text-[15px] font-medium text-ink-900">패스키</p>
      <p className="mt-1 text-[13px] leading-relaxed text-ink-500">
        이 기기에 패스키를 등록하면 다음부터 Face ID·지문·기기 잠금으로 인증번호 없이 로그인해요.
      </p>

      {list && list.length > 0 && (
        <ul className="mt-4 space-y-2">
          {list.map((pk) => (
            <li
              key={pk.id}
              className="flex items-center justify-between rounded-xl bg-ink-50 px-3.5 py-2.5"
            >
              <div>
                <p className="text-[13px] font-medium text-ink-800">{pk.deviceLabel ?? '패스키'}</p>
                <p className="text-[11px] text-ink-400">
                  {new Date(pk.createdAt).toLocaleDateString('ko-KR')} 등록
                  {pk.lastUsedAt
                    ? ` · 최근 ${new Date(pk.lastUsedAt).toLocaleDateString('ko-KR')}`
                    : ''}
                </p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                disabled={busy}
                onClick={() => void remove(pk.id)}
              >
                삭제
              </Button>
            </li>
          ))}
        </ul>
      )}

      <div className="mt-4">
        <Button size="sm" disabled={busy} onClick={() => void add()}>
          {busy ? '등록 중…' : '이 기기에 패스키 등록'}
        </Button>
      </div>

      <Notice tone="info">{note}</Notice>
      <Notice>{error}</Notice>
    </div>
  );
}

function ProfileView({ me, onEdit }: { me: MeUser; onEdit: () => void }) {
  return (
    <div className="rounded-2xl bg-surface p-5 shadow-card">
      <div className="flex items-start justify-between">
        <p className="text-[15px] font-medium text-ink-900">내 정보</p>
        <Button variant="secondary" size="sm" onClick={onEdit}>
          수정
        </Button>
      </div>
      <dl className="mt-3 space-y-2 text-[13px]">
        <div className="flex justify-between">
          <dt className="text-ink-400">활동명</dt>
          <dd className="text-ink-700">{me.nickname}</dd>
        </div>
        <div className="flex justify-between">
          <dt className="flex items-center gap-1 text-ink-400">
            토마토
            {/* 마우스를 올리면(또는 포커스하면) 안내가 뜬다. */}
            <span className="group relative inline-flex">
              <span
                tabIndex={0}
                aria-label="토마토란?"
                className="flex size-3.5 cursor-help items-center justify-center rounded-full bg-ink-200 text-[9px] font-bold text-white"
              >
                ?
              </span>
              <span className="pointer-events-none absolute bottom-full left-0 z-10 mb-1.5 w-44 whitespace-normal rounded-lg bg-ink-900 px-2.5 py-1.5 text-[11px] leading-relaxed text-ink-50 opacity-0 shadow-lg transition duration-150 group-hover:opacity-100 group-focus-within:opacity-100">
                토마토는 어디에 쓸 줄은 모르겠는데 그냥 만들었어요 🍅
              </span>
            </span>
          </dt>
          <dd className="font-medium text-tomato-600">{formatTomato(me.tomatoBalance)}</dd>
        </div>
        <div className="flex justify-between">
          <dt className="text-ink-400">이메일</dt>
          <dd className="text-ink-700">{me.email}</dd>
        </div>
        <div className="flex justify-between">
          <dt className="text-ink-400">계열사</dt>
          <dd className="text-ink-700">
            {me.affiliation ? AFFILIATION_LABELS[me.affiliation] : '선택 안 함'}
            {me.affiliation && (
              <span className="ml-1.5 text-ink-400">
                {me.affiliationVisible ? '(공개)' : '(비공개)'}
              </span>
            )}
          </dd>
        </div>
      </dl>
      <p className="mt-3 text-[12px] text-ink-400">
        이메일은 로그인 아이디라 바꿀 수 없어요. 다른 사람에게는 활동명만 보입니다.
      </p>
    </div>
  );
}

function ProfileEdit({ me, onDone }: { me: MeUser; onDone: () => void }) {
  const updateMe = useSessionStore((s) => s.updateMe);

  const [nickname, setNickname] = useState(me.nickname);
  const [affiliation, setAffiliation] = useState<Affiliation | ''>(me.affiliation ?? '');
  const [visible, setVisible] = useState(me.affiliationVisible);
  const [availability, setAvailability] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const changed = nickname !== me.nickname;
  const check = checkNickname(nickname);
  const localError = nickname && !check.ok ? NICKNAME_ERROR_MESSAGES[check.error!] : '';

  // 활동명을 바꿨고 형식이 맞을 때만 중복 확인(디바운스). 안 바꿨으면 물어볼 필요 없다.
  useEffect(() => {
    setAvailability('');
    if (!changed || !check.ok) return;
    const t = setTimeout(() => {
      void api
        .get<{ available: boolean; message?: string }>(
          `/api/nickname/available?value=${encodeURIComponent(check.value)}`,
        )
        .then((res) => setAvailability(res.available ? '' : (res.message ?? '')))
        .catch(() => {});
    }, 400);
    return () => clearTimeout(t);
  }, [changed, check.ok, check.value]);

  useEffect(() => {
    if (!affiliation) setVisible(false);
  }, [affiliation]);

  const canSave = check.ok && !availability && !busy;

  async function save() {
    if (!canSave) return;
    setBusy(true);
    setError('');
    try {
      const res = await api.patch<{ user: MeUser }>('/api/me', {
        nickname: check.value,
        affiliation: affiliation || null,
        affiliationVisible: visible,
      });
      updateMe(res.user);
      onDone();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : '저장하지 못했어요.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="rounded-2xl bg-surface p-5 shadow-card">
      <p className="text-[15px] font-medium text-ink-900">내 정보 수정</p>

      <div className="mt-4">
        <Label hint={`${nicknameWidth(check.value)}/${NICKNAME_RULES.maxWidth}칸`}>활동명</Label>
        <TextField value={nickname} disabled={busy} onChange={(e) => setNickname(e.target.value)} />
        <p className="mt-1.5 text-xs text-ink-400">
          영문 5자 또는 한글 2자부터. 한글 1자는 2칸이에요.
          {me.role !== 'admin' &&
            ` 활동명은 ${NICKNAME_CHANGE_LIMIT.weeks}주에 ${NICKNAME_CHANGE_LIMIT.max}번까지 바꿀 수 있어요.`}
        </p>
        {(localError || availability) && (
          <p className="mt-1.5 text-xs text-rose-600">{localError || availability}</p>
        )}
      </div>

      <div className="mt-4">
        <Label hint="선택">계열사</Label>
        <Select
          value={affiliation}
          disabled={busy}
          onChange={(e) => setAffiliation(e.target.value as Affiliation | '')}
        >
          <option value="">선택 안 함</option>
          {AFFILIATIONS.map((code) => (
            <option key={code} value={code}>
              {AFFILIATION_LABELS[code]}
            </option>
          ))}
        </Select>
        <div className="mt-2.5">
          <Checkbox checked={visible} disabled={!affiliation || busy} onChange={setVisible}>
            다른 사람에게 계열사를 공개할래요
          </Checkbox>
        </div>
        <p className="mt-1.5 text-xs text-ink-400">공개하지 않으면 서버 응답에서 아예 빠집니다.</p>
      </div>

      <div className="mt-5 flex gap-2">
        <Button className="flex-1" disabled={!canSave} onClick={() => void save()}>
          {busy ? '저장 중…' : '저장'}
        </Button>
        <Button variant="secondary" disabled={busy} onClick={onDone}>
          취소
        </Button>
      </div>
      <Notice>{error}</Notice>
    </div>
  );
}

/**
 * 스테이블코인 지갑 — 주소 등록/수정/삭제 + 복사·QR.
 * 주소는 본인과 1:1 대화 상대에게만 보인다(글·프로필 공개 아님).
 */
function WalletSettingsPanel() {
  const me = useSessionStore((s) => s.me);
  const updateMe = useSessionStore((s) => s.updateMe);
  const address = me?.walletAddress ?? null;

  const [editing, setEditing] = useState(false);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const check = input.trim() ? checkWalletAddress(input) : null;
  const localError = check && !check.ok ? check.message : '';

  async function save(next: string | null) {
    setBusy(true);
    setError('');
    try {
      const res = await api.patch<{ user: MeUser }>('/api/me', { walletAddress: next });
      updateMe(res.user);
      setEditing(false);
      setInput('');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : '저장하지 못했어요.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="rounded-2xl bg-surface p-5 shadow-card">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-[15px] font-medium text-ink-900">💳 가상자산 지갑</p>
          <p className="mt-1 text-[13px] text-ink-500">
            사내 스테이블코인 지갑주소를 등록하면 거래 대화방에서 상대가 바로 복사해 송금할 수
            있어요.
          </p>
        </div>
        {address && !editing && (
          <Button
            variant="secondary"
            size="sm"
            onClick={() => {
              setInput(address);
              setEditing(true);
            }}
          >
            수정
          </Button>
        )}
      </div>

      {!editing && address && (
        <div className="mt-3">
          <WalletPanel address={address} />
          <button
            disabled={busy}
            onClick={() => void save(null)}
            className="mt-2 text-[12px] text-ink-400 underline-offset-2 transition hover:text-rose-600 hover:underline"
          >
            등록 해제
          </button>
        </div>
      )}

      {!editing && !address && (
        <Button className="mt-3" variant="secondary" size="sm" onClick={() => setEditing(true)}>
          지갑주소 등록
        </Button>
      )}

      {editing && (
        <div className="mt-3">
          <TextField
            value={input}
            disabled={busy}
            placeholder="0x…"
            maxLength={WALLET_ADDRESS_MAX}
            onChange={(e) => setInput(e.target.value)}
          />
          {localError && <p className="mt-1.5 text-xs text-rose-600">{localError}</p>}
          <p className="mt-1.5 text-xs text-ink-400">
            지갑 앱에서 내 주소를 복사해 붙여넣으세요. 주소가 틀리면 송금을 되돌릴 수 없으니 꼭
            확인해 주세요.
          </p>
          <div className="mt-3 flex gap-2">
            <Button
              className="flex-1"
              disabled={busy || !input.trim() || Boolean(localError)}
              onClick={() => check?.ok && void save(check.value)}
            >
              {busy ? '저장 중…' : '저장'}
            </Button>
            <Button
              variant="secondary"
              disabled={busy}
              onClick={() => {
                setEditing(false);
                setInput('');
                setError('');
              }}
            >
              취소
            </Button>
          </div>
        </div>
      )}
      <p className="mt-3 text-[12px] text-ink-400">
        주소는 다른 사람의 글·프로필에는 보이지 않고, 나와 1:1 대화 중인 상대에게만 보여요.
      </p>
      <Notice>{error}</Notice>
    </div>
  );
}

/** 관리자에게 제안하기 — 누구나 한 줄 건의. 관리 화면에서 확인한다. */
function SuggestPanel() {
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  async function submit() {
    if (!body.trim() || busy) return;
    setBusy(true);
    setError('');
    try {
      await api.post('/api/suggestions', { body: body.trim() });
      setBody('');
      setDone(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : '보내지 못했어요.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="rounded-2xl bg-surface p-5 shadow-card">
      <p className="text-[15px] font-medium text-ink-900">관리자에게 제안하기</p>
      <p className="mt-1 text-[13px] leading-relaxed text-ink-500">
        불편한 점, 있으면 좋겠는 기능, 뭐든 편하게 남겨주세요. 활동명과 함께 운영자에게 전달돼요.
      </p>

      {done ? (
        <div className="mt-4">
          <p className="text-[13px] text-tomato-600">보냈어요. 고맙습니다 🍅</p>
          <Button
            variant="ghost"
            size="sm"
            className="mt-1 px-0"
            onClick={() => setDone(false)}
          >
            하나 더 쓰기
          </Button>
        </div>
      ) : (
        <div className="mt-3">
          <TextArea
            rows={3}
            maxLength={SUGGESTION_LIMITS.maxLength}
            placeholder="예: 검색에 카테고리 필터가 있으면 좋겠어요"
            value={body}
            disabled={busy}
            onChange={(e) => setBody(e.target.value)}
          />
          <div className="mt-2.5 flex justify-end">
            <Button size="sm" disabled={busy || !body.trim()} onClick={() => void submit()}>
              {busy ? '보내는 중…' : '보내기'}
            </Button>
          </div>
          <Notice>{error}</Notice>
        </div>
      )}
    </div>
  );
}

function ProfilePanel() {
  const me = useSessionStore((s) => s.me);
  const [editing, setEditing] = useState(false);
  if (!me) return null;
  return editing ? (
    <ProfileEdit me={me} onDone={() => setEditing(false)} />
  ) : (
    <ProfileView me={me} onEdit={() => setEditing(true)} />
  );
}

/** 계정 탈퇴 — 비활성화 + 익명화. 2단계 확인. */
function WithdrawPanel() {
  const logout = useSessionStore((s) => s.logout);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  async function withdraw() {
    setBusy(true);
    setError('');
    try {
      await api.del('/api/me');
      await logout(); // 세션이 사라졌으니 로그인 화면으로
    } catch (err) {
      setError(err instanceof ApiError ? err.message : '탈퇴하지 못했어요.');
      setBusy(false);
    }
  }

  return (
    <div className="rounded-2xl bg-surface p-5 shadow-card">
      <p className="text-[15px] font-medium text-ink-900">계정 탈퇴</p>
      <p className="mt-1 text-[13px] leading-relaxed text-ink-500">
        탈퇴하면 활동명·계열사·연동 정보와 패스키가 지워지고, 이메일도 우리 기록에서 제거돼요. 작성한
        글·댓글도 함께 삭제돼요. 원하면 나중에 다시 가입할 수 있어요.
      </p>

      {confirming ? (
        <div className="mt-4 rounded-xl bg-tomato-50 p-3.5">
          <p className="text-[13px] text-tomato-700">정말 탈퇴할까요? 이 작업은 되돌릴 수 없어요.</p>
          <div className="mt-3 flex gap-2">
            <Button variant="danger" size="sm" disabled={busy} onClick={() => void withdraw()}>
              {busy ? '처리 중…' : '네, 탈퇴할게요'}
            </Button>
            <Button variant="ghost" size="sm" disabled={busy} onClick={() => setConfirming(false)}>
              취소
            </Button>
          </div>
        </div>
      ) : (
        <Button variant="secondary" size="sm" className="mt-4" onClick={() => setConfirming(true)}>
          탈퇴하기
        </Button>
      )}
      <Notice>{error}</Notice>
    </div>
  );
}

/**
 * 화면 테마 — 시스템/라이트/다크 3단 세그먼트.
 * 기본은 시스템을 따르고, 고정하면 localStorage에 남아 다음 방문에도 유지된다.
 */
function ThemePanel() {
  const [choice, setChoice] = useState<ThemeChoice>(getThemeChoice);

  function select(next: ThemeChoice) {
    setChoice(next);
    setThemeChoice(next);
  }

  const OPTIONS: { value: ThemeChoice; label: string }[] = [
    { value: 'system', label: '시스템' },
    { value: 'light', label: '라이트' },
    { value: 'dark', label: '다크' },
  ];

  return (
    <div className="rounded-2xl bg-surface p-5 shadow-card">
      <p className="text-[15px] font-medium text-ink-900">화면 테마</p>
      <p className="mt-1 text-[13px] text-ink-500">
        시스템을 따르거나, 밝기와 상관없이 고정할 수 있어요.
      </p>
      <div className="mt-3 flex gap-1 rounded-xl bg-ink-100 p-1">
        {OPTIONS.map((opt) => (
          <button
            key={opt.value}
            type="button"
            onClick={() => select(opt.value)}
            className={`flex-1 rounded-lg py-2 text-[13px] font-medium transition ${
              choice === opt.value
                ? 'bg-surface text-ink-900 shadow-sm'
                : 'text-ink-500 hover:text-ink-700'
            }`}
          >
            {opt.label}
          </button>
        ))}
      </div>
    </div>
  );
}

/** 바로가기 라인 아이콘 — 하단 탭바와 같은 굵기·스타일. */
function QuickIcon({ children }: { children: ReactNode }) {
  return (
    <svg
      viewBox="0 0 24 24"
      className="h-[18px] w-[18px] text-ink-500"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      {children}
    </svg>
  );
}

/**
 * 모바일 전용 바로가기 + 로그아웃.
 * 하단 탭바로 개편하며 상단 헤더에서 빠진 찜·따기·로그아웃이 여기로 왔다.
 * 데스크톱은 상단 헤더에 다 있으므로 sm 이상에선 숨긴다.
 */
function QuickLinksPanel() {
  const me = useSessionStore((s) => s.me);
  const logout = useSessionStore((s) => s.logout);

  const linkCls =
    'flex items-center justify-between rounded-xl bg-ink-50 px-4 py-3 text-[14px] text-ink-800 transition active:scale-[0.98]';

  return (
    <div className="rounded-2xl bg-surface p-5 shadow-card sm:hidden">
      <div className="flex items-center justify-between">
        <p className="text-[15px] font-medium text-ink-900">{me?.nickname}</p>
        <button
          type="button"
          onClick={() => void logout()}
          className="cursor-pointer text-[13px] text-ink-400 transition hover:text-ink-700"
        >
          로그아웃
        </button>
      </div>
      <div className="mt-3 space-y-2">
        {/* 아이콘은 하단 탭바와 같은 라인 스타일 — 이모지 혼용을 피한다(아이콘 언어 일관성). */}
        <Link viewTransition to="/favorites" className={linkCls}>
          <span className="flex items-center gap-2.5">
            <QuickIcon>
              <path d="M12 20s-7-4.6-9-9c-1.2-2.7.6-6 3.8-6 2 0 3.4 1.2 5.2 3.2C13.8 6.2 15.2 5 17.2 5c3.2 0 5 3.3 3.8 6-2 4.4-9 9-9 9Z" />
            </QuickIcon>
            찜한 상품
          </span>
          <span className="text-ink-300">›</span>
        </Link>
        <Link viewTransition to="/chats" className={linkCls}>
          <span className="flex items-center gap-2.5">
            <QuickIcon>
              <path d="M20 11.5c0 3.6-3.6 6.5-8 6.5-.9 0-1.8-.1-2.6-.35L5 19l.8-2.9C4.7 15 4 13.3 4 11.5 4 7.9 7.6 5 12 5s8 2.9 8 6.5Z" />
            </QuickIcon>
            대화
          </span>
          <span className="text-ink-300">›</span>
        </Link>
        <Link viewTransition to="/harvest" className={linkCls}>
          <span className="flex items-center gap-2.5">
            <QuickIcon>
              <ellipse cx="12" cy="14" rx="7.5" ry="6.5" />
              <path d="M12 7.5c-1.6-1.4-3.6-1.6-5-1 1.6.8 3.4 1.2 5 1Zm0 0c1.6-1.4 3.6-1.6 5-1-1.6.8-3.4 1.2-5 1Zm0 0V4.5" />
            </QuickIcon>
            토마토 따기
          </span>
          <span className="text-ink-300">›</span>
        </Link>
      </div>
    </div>
  );
}

export function SettingsScreen() {
  return (
    <div className="py-6">
      <h1 className="mb-5 text-[26px] font-semibold tracking-tight text-ink-900">
        {/* 모바일 탭 이름(마이페이지)과 데스크톱 진입점(설정)을 각각 따른다. */}
        <span className="sm:hidden">마이페이지</span>
        <span className="max-sm:hidden">설정</span>
      </h1>
      <div className="space-y-4">
        <QuickLinksPanel />
        <ThemePanel />
        <ProfilePanel />
        <WalletSettingsPanel />
        <InvitePanel />
        <InstallPanel />
        <PasskeyPanel />
        <TelegramPanel />
        <NotifPanel />
        <SuggestPanel />
        <WithdrawPanel />
      </div>
    </div>
  );
}
